/**
 * Écran de choix d'une carte parmi trois (M2-07, DECISIONS §7 F11/F13).
 *
 * Ce que le serveur envoie, et rien de plus :
 * - une carte **débloquée** (`effect` renseigné) ;
 * - deux cartes **verrouillées** (`effect: null`) : leur `descKey`, leurs `tags` et leurs effets
 *   ne transitent pas avant acquisition. L'écran affiche donc le nom et la rareté, jamais l'effet.
 *
 * Le nom d'une carte est tronqué à l'affichage au-delà de 18 caractères (A-13), avec le libellé
 * complet en `title` et `aria-label` : aucune information n'est perdue pour l'accessibilité.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IconeElement } from './IconeElement';
import { cleTraductionContenu } from '../i18n';

import type { OptionCarte } from '@elemora/shared/api';
import type { Element } from '@elemora/shared/content';
import type { TFunction } from 'i18next';

/** Longueur maximale du nom affiché (A-13). */
export const LONGUEUR_NOM_CARTE = 18;

/** Tronque un nom trop long en conservant un libellé lisible (A-13). */
export function tronquerNom(nom: string, limite = LONGUEUR_NOM_CARTE): string {
  if (nom.length <= limite) return nom;
  return `${nom.slice(0, limite - 1).trimEnd()}…`;
}

/** Une valeur de pourcentage signée, ex. `+10 %` / `-5 %`. */
function pourcentage(valeur: number): string {
  return `${valeur >= 0 ? '+' : ''}${String(valeur)} %`;
}

/**
 * Résumé lisible d'un effet de carte, à partir du JSON publié.
 *
 * Le client ne connaît pas la sémantique complète du DSL : il se contente de nommer le champ
 * touché et sa valeur. Un effet inconnu est signalé comme tel plutôt que masqué.
 */
export function resumeEffet(effet: unknown, t: TFunction): string {
  if (typeof effet !== 'object' || effet === null) return t('jeu.cartes.effet.inconnu');
  const champs = effet as Record<string, unknown>;
  const type = champs['type'];
  const valeur = typeof champs['valeur'] === 'number' ? champs['valeur'] : 0;

  switch (type) {
    case 'bonusStat': {
      const stat = String(champs['stat'] ?? '');
      const cle = `jeu.cartes.effet.stat.${stat}`;
      return t(cle, { valeur: pourcentage(valeur), defaultValue: t('jeu.cartes.effet.inconnu') });
    }
    case 'bonusEffet': {
      const element = String(champs['element'] ?? '');
      return t('jeu.cartes.effet.elementaire', {
        element: t(`content:element.${element}.name`, { defaultValue: element }),
        valeur: pourcentage(valeur),
      });
    }
    case 'bonusEconomie': {
      const cible = String(champs['cible'] ?? '');
      return t(`jeu.cartes.effet.economie.${cible}`, {
        valeur: pourcentage(valeur),
        defaultValue: t('jeu.cartes.effet.inconnu'),
      });
    }
    case 'ressource': {
      const cible = String(champs['cible'] ?? '');
      return t(`jeu.cartes.effet.ressource.${cible}`, {
        nombre: valeur,
        defaultValue: t('jeu.cartes.effet.inconnu'),
      });
    }
    case 'pvParcours':
      return t('jeu.cartes.effet.pv', { nombre: valeur });
    case 'synergie':
      return t('jeu.cartes.effet.synergie');
    default:
      return t('jeu.cartes.effet.inconnu');
  }
}

/** Vrai si l'option est déjà débloquée : le serveur joint alors son effet complet. */
/**
 * Élément d'une carte, lu dans son identifiant (`carte_<élément>_<numéro>`, convention imposée
 * par `docs/contenu.md` §2). `null` pour une carte neutre ou un identifiant hors convention.
 */
export function elementDeCarte(cardId: string): Element | null {
  const trouve = /^carte_(feu|eau|terre|air)_/.exec(cardId);
  return trouve === null ? null : (trouve[1] as Element);
}

export function optionDebloquee(option: OptionCarte): boolean {
  return option.effect !== null;
}

export interface ProprietesPanneauCartes {
  readonly options: readonly OptionCarte[];
  /** Carte bonus de boss accordée avec cette vague, `null` s'il n'y en a pas. */
  readonly bonusCardId: string | null;
  /** Choix en cours (verrou d'interface). */
  readonly enCours: boolean;
  readonly onChoisir: (cardId: string) => void;
}

export function PanneauCartes({
  options,
  bonusCardId,
  enCours,
  onChoisir,
}: ProprietesPanneauCartes) {
  const { t } = useTranslation();
  const liste = useRef<HTMLUListElement>(null);

  // Fenêtre au-dessus du plateau : le focus va sur la première carte, le clavier choisit sans
  // détour (le choix est obligatoire, la fenêtre ne se ferme pas autrement).
  useEffect(() => {
    liste.current?.querySelector<HTMLButtonElement>('.carte-option:not(:disabled)')?.focus();
  }, []);

  return (
    <section
      className="panneau panneau--cartes"
      role="dialog"
      aria-modal="true"
      aria-labelledby="titre-cartes"
      data-cartes="choix"
    >
      <h2 className="panneau__titre" id="titre-cartes">
        {t('jeu.cartes.titre')}
      </h2>
      <p className="note">{t('jeu.cartes.aide')}</p>
      {bonusCardId === null ? null : (
        <p className="note note--accent" data-carte-bonus={bonusCardId}>
          {t('jeu.cartes.bonus', {
            nom: t(cleTraductionContenu(`content.carte.${bonusCardId}.name`)),
          })}
        </p>
      )}
      <ul className="cartes" ref={liste}>
        {options.map((option) => {
          const nomComplet = t(cleTraductionContenu(option.nameKey));
          const nom = tronquerNom(nomComplet);
          const debloquee = optionDebloquee(option);
          const element = elementDeCarte(option.cardId);
          const effets = Array.isArray(option.effect)
            ? option.effect.map((effet) => resumeEffet(effet, t))
            : [];
          return (
            <li key={option.cardId} className="cartes__item">
              <button
                type="button"
                className={`carte-option${debloquee ? ' carte-option--debloquee' : ''}`}
                data-card={option.cardId}
                data-debloquee={debloquee ? 'oui' : 'non'}
                data-rarete={option.rarity}
                data-element={debloquee && element !== null ? element : undefined}
                disabled={enCours}
                aria-label={t('jeu.cartes.choisir', { nom: nomComplet })}
                title={nomComplet}
                onClick={() => {
                  onChoisir(option.cardId);
                }}
              >
                {/*
                  Emblème décoratif : l'élément pour une carte de la collection, un sceau pour
                  une carte à découvrir (on ne dévoile rien de plus que son nom).
                */}
                <span
                  className={`carte-option__embleme${debloquee ? '' : ' carte-option__embleme--scelle'}`}
                  aria-hidden="true"
                >
                  {debloquee && element !== null ? <IconeElement element={element} /> : null}
                </span>
                <span className="carte-option__rarete">
                  {t(`content:rarity.${option.rarity}.name`, { defaultValue: option.rarity })}
                </span>
                <span className="carte-option__nom">{nom}</span>
                {debloquee ? (
                  <span className="carte-option__effets">
                    {effets.length === 0 ? t('jeu.cartes.effet.inconnu') : effets.join(' · ')}
                  </span>
                ) : (
                  <span className="carte-option__effets carte-option__effets--masque">
                    {t('jeu.cartes.effetMasque')}
                  </span>
                )}
                <span className="carte-option__etat">
                  {debloquee ? t('jeu.cartes.debloquee') : t('jeu.cartes.verrouillee')}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
